import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Field, FieldSet } from '../../components/Form';
import FormModal from '../../components/FormModal';
import CREATE_STATIC_TOKEN_INTEGRATION from '../../graphql/createStaticTokenIntegration';
import Link from '../../utils/router/Link';
import * as styles from './GitlabInstructionModal.module.scss';

const HOST_BY_GITLAB = 'gitlab';
const HOST_BY_SELF = 'self';

interface GitlabInstructionModalProps {
  close: () => void;
}

const GitlabIntegrationInstructionModal = ({
  close,
}: GitlabInstructionModalProps) => {
  const [integrate] = useMutation(CREATE_STATIC_TOKEN_INTEGRATION);

  const [host, setHost] = useState<string | null>(null);
  const handleSubmit = ({ token }: { token: string }) =>
    integrate({ variables: { host, token, platform: 'gitlab' } }).then(() => {
      window.location.reload();
    });

  const sharedModalProps = {
    title: 'Integrate with GitLab',
    close,
    width: 425,
    variant: 'action' as const,
  };

  return host ? (
    <FormModal<{ token: string }>
      key="token" // ensure Form is reinitialize and  `initialValues` is applied
      form={{
        initialValues: { token: '' },
        onSubmit: handleSubmit,
        apiErrorMessages: {
          INVALID: `We couldn't access your repos at ${host}, please make sure the token is correct and has the right scope.`,
        },
      }}
      footer={{
        submitButton: { text: 'Integrate' },
        leftButton: {
          text: 'Back',
          onClick: () => setHost(null),
          variant: 'secondary',
        },
      }}
      {...sharedModalProps}
    >
      Go to your{' '}
      <Link to={`https://${host}/-/profile/personal_access_tokens`} newTab>
        Access Tokens
      </Link>{' '}
      settings page, and generate a token with the following settings:
      <ul>
        <li>
          <strong>Name</strong>: Slab Integration
        </li>
        <li>
          <strong>Expires at</strong>: Leave as blank
        </li>
        <li>
          <strong>Scopes</strong>: Select <b>api</b>
        </li>
      </ul>
      <div className={styles.copyPaste}>
        Then copy and paste the new personal access token:
      </div>
      <Field
        placeholder="Your token"
        name="token"
        type="text"
        required
        autoComplete="off"
      />
    </FormModal>
  ) : (
    <FormModal
      form={{
        initialValues: { hostBy: HOST_BY_GITLAB, selfHostUrl: '' },
        onSubmit: ({ hostBy, selfHostUrl }) => {
          setHost(hostBy !== HOST_BY_GITLAB ? selfHostUrl : 'gitlab.com');
        },
      }}
      footer={{
        submitButton: { text: 'Next' },
      }}
      {...sharedModalProps}
    >
      {({ values: { hostBy } }) => (
        <>
          <FieldSet legend="Where is your GitLab installation hosted at?">
            <Field
              type="radio"
              name="hostBy"
              value={HOST_BY_GITLAB}
              label="GitLab.com"
            />
            <Field
              type="radio"
              name="hostBy"
              value={HOST_BY_SELF}
              label="Self-Managed"
            />
          </FieldSet>
          {hostBy === HOST_BY_SELF && (
            <>
              <Field
                name="selfHostUrl"
                placeholder="gitlab.slab.com"
                margin="compact"
                prepend="https://"
                label="Your GitLab Server URL"
                required
              />
              <div>
                Your server needs to be reachable from the public Internet.
              </div>
            </>
          )}
        </>
      )}
    </FormModal>
  );
};

export default GitlabIntegrationInstructionModal;
